import { ref, type Ref } from 'vue'
import * as L from 'leaflet'
import ConstituencyShapeService from '../services/constituency-shape.service'

export interface UseConstituencyShapeOptions {
    map: Ref<any>
    constituencyShapeService?: ConstituencyShapeService
}

export function useConstituencyShape(options: UseConstituencyShapeOptions) {
    const { map, constituencyShapeService = new ConstituencyShapeService() } = options

    const constituencyShapeLayer = ref<L.GeoJSON | null>(null)
    const activeConstituencyShape = ref<string | null>(null)

    function clearConstituencyShape() {
        if (constituencyShapeLayer.value && map.value) {
            map.value.removeLayer(constituencyShapeLayer.value as unknown as L.Layer)
            constituencyShapeLayer.value = null
            activeConstituencyShape.value = null
        }
    }

    async function loadConstituencyShape(constituencyName: string) {
        try {
            // Clear any existing shape layer
            clearConstituencyShape()

            // Load the constituency shape file
            const result = await constituencyShapeService.getConstituencyShape(constituencyName)

            result.fold(
                (geoJson) => {
                    if (map.value) {
                        // Create a new GeoJSON layer for the constituency shape
                        constituencyShapeLayer.value = L.geoJSON(geoJson as any, {
                            style: {
                                color: '#f0ece9',
                                weight: 3,
                                fillColor: '#f0ece9',
                                fillOpacity: 0.2
                            },
                            onEachFeature: (feature, layer) => {
                                // Add tooltip with polling station info
                                if (feature.properties) {
                                    const props = feature.properties
                                    const tooltipContent = `
                    <div>
                      <strong>${props['Polling Station Name'] || 'Unknown'}</strong><br>
                      <strong>Code:</strong> ${props['Code'] || 'N/A'}<br>
                      <strong>EA:</strong> ${props['EA Name'] || 'N/A'}<br>
                      <strong>Voters:</strong> ${props['Voters'] || 'N/A'}
                    </div>
                  `
                                    layer.bindTooltip(tooltipContent, { direction: 'top' })
                                }

                                // Add hover effects
                                layer.on({
                                    mouseover: (e: L.LeafletMouseEvent) => {
                                        const hoveredLayer = e.target as L.Path
                                        hoveredLayer.setStyle({
                                            color: '#FF8C42',
                                            weight: 4,
                                            fillColor: '#FF8C42',
                                            fillOpacity: 0.4
                                        })
                                    },
                                    mouseout: (e: L.LeafletMouseEvent) => {
                                        const hoveredLayer = e.target as L.Path
                                        hoveredLayer.setStyle({
                                            color: '#f0ece9',
                                            weight: 3,
                                            fillColor: '#f0ece9',
                                            fillOpacity: 0.2
                                        })
                                    }
                                })
                            }
                        })

                        // Add the layer to the map
                        constituencyShapeLayer.value.addTo(map.value as any)
                        activeConstituencyShape.value = constituencyName

                        // Zoom to the constituency shape bounds
                        const bounds = constituencyShapeLayer.value.getBounds()
                        if (bounds.isValid()) {
                            map.value.fitBounds(bounds, { padding: [20, 20] })
                        }
                    }
                },
                (error) => {
                    console.warn(`Could not load shape file for ${constituencyName}:`, error.message)
                }
            )
        } catch (error) {
            console.error('Error loading constituency shape:', error)
        }
    }

    return {
        constituencyShapeLayer,
        activeConstituencyShape,
        clearConstituencyShape,
        loadConstituencyShape
    }
}
